/* eslint-disable no-unused-vars, react-hooks/exhaustive-deps */
import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import SEOHead from "../../components/SEO/SEOHead";
import { fetchAllPublishedEvents } from "../../firebaseFunctions/mpsDashboard/mpsDashboard";
import logo from "../../assets/images/pebble-logo.png";
import placeholderImage from "../../assets/images/no_image_placeholder.jpg";
import {
  MapPin,
  ExternalLink,
  ArrowRight,
  Navigation,
  X,
  Calendar,
  CheckCircle,
  Users,
  Store,
  Search,
} from "lucide-react";
import LanguageSwitcher from "../../components/LanguageSwitcher";
import DarkModeToggle from "../../components/ui/DarkModeToggle";
import { useTranslation } from "react-i18next";

const BrowseEvents = () => {
  const { t } = useTranslation();
  const [events, setEvents] = useState([]);
  const [filteredEvents, setFilteredEvents] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [userLocation, setUserLocation] = useState(null);
  const [locationPermission, setLocationPermission] = useState(null);
  const [showLocationPrompt, setShowLocationPrompt] = useState(false);
  const [expandedSellerTypes, setExpandedSellerTypes] = useState({});
  const [searchQuery, setSearchQuery] = useState("");
  const navigate = useNavigate();

  const handleSearch = (query) => {
    setSearchQuery(query);
    if (!query.trim()) {
      setFilteredEvents(events);
      return;
    }
    const lowerQuery = query.toLowerCase();
    const filtered = events.filter(
      (event) =>
        event.eventName?.toLowerCase().includes(lowerQuery) ||
        event.location?.toLowerCase().includes(lowerQuery) ||
        event.eventType?.toLowerCase().includes(lowerQuery) ||
        event.city?.toLowerCase().includes(lowerQuery) ||
        event.state?.toLowerCase().includes(lowerQuery) ||
        event.description?.toLowerCase().includes(lowerQuery) ||
        event.eventDescription?.toLowerCase().includes(lowerQuery),
    );
    setFilteredEvents(filtered);
  };

  const calculateDistance = (lat1, lon1, lat2, lon2) => {
    const R = 3959;
    const dLat = ((lat2 - lat1) * Math.PI) / 180;
    const dLon = ((lon2 - lon1) * Math.PI) / 180;
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos((lat1 * Math.PI) / 180) *
        Math.cos((lat2 * Math.PI) / 180) *
        Math.sin(dLon / 2) *
        Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  };

  const requestLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setUserLocation({
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          });
          setLocationPermission("granted");
          setShowLocationPrompt(false);
        },
        (error) => {
          console.error("Error getting location:", error);
          setLocationPermission("denied");
          setShowLocationPrompt(false);
        },
      );
    } else {
      setLocationPermission("unsupported");
      setShowLocationPrompt(false);
    }
  };

  useEffect(() => {
    const loadEvents = async () => {
      try {
        const publishedEvents = await fetchAllPublishedEvents();
        setEvents(publishedEvents);
        setFilteredEvents(publishedEvents);

        if (
          publishedEvents.length > 0 &&
          !userLocation &&
          locationPermission === null
        ) {
          setShowLocationPrompt(true);
        }
      } catch (error) {
        console.error("Error loading events:", error);
        setEvents([]);
        setFilteredEvents([]);
      } finally {
        setIsLoading(false);
      }
    };

    loadEvents();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (userLocation && events.length > 0) {
      const eventsWithDistance = events.map((event) => {
        if (event.latitude && event.longitude) {
          const distance = calculateDistance(
            userLocation.lat,
            userLocation.lng,
            event.latitude,
            event.longitude,
          );
          return { ...event, distance };
        }
        return { ...event, distance: null };
      });

      eventsWithDistance.sort((a, b) => {
        if (a.distance === null) return 1;
        if (b.distance === null) return -1;
        return a.distance - b.distance;
      });

      setFilteredEvents(eventsWithDistance);
    }
  }, [userLocation, events]);

  const handleEventClick = (eventId) => {
    // Show login/signup modal when trying to view event details
    document.getElementById("auth-modal").showModal();
  };

  return (
    <div className="min-h-screen bg-grey-50">
      <SEOHead
        title="Browse Events"
        description="Discover local festivals, farmers markets, craft fairs, and more. Find events near you and buy tickets or apply as a vendor on Pebble."
        url="https://app.usepebble.app/browse-events"
      />
      {/* Floating Header with Language Switcher */}
      <div className="fixed top-0 right-0 z-50 flex items-center gap-2 p-3">
        <DarkModeToggle variant="icon" />
        <LanguageSwitcher />
      </div>

      {/* Hero Section - Mobile Optimized */}
      <div className="bg-primary-400 text-white py-8 sm:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-2xl sm:text-4xl font-bold text-center mb-2 sm:mb-4">
            {t("pages.browseEvents.title")}
          </h1>
          <p className="text-base sm:text-xl text-center max-w-3xl mx-auto mb-6">
            {t("pages.browseEvents.subtitle")}
          </p>

          {/* Search Bar - Prominent on Mobile */}
          <div className="max-w-xl mx-auto">
            <div className="relative">
              <Search
                className="absolute left-4 top-1/2 transform -translate-y-1/2 text-grey-400"
                size={20}
              />
              <input
                type="text"
                placeholder={t("pages.browseEvents.searchPlaceholder")}
                value={searchQuery}
                onChange={(e) => handleSearch(e.target.value)}
                className="w-full pl-12 pr-4 py-4 rounded-xl text-grey-900 placeholder-grey-500 focus:outline-none focus:ring-2 focus:ring-white shadow-lg text-base"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Quick Action Banner for Vendors - Mobile */}
      <div className="bg-blue-50 border-b border-blue-100 py-3 px-4 sm:hidden">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Store size={18} className="text-blue-600" />
            <span className="text-sm font-medium text-blue-900">
              {t("pages.browseEvents.wantToSell")}
            </span>
          </div>
          <button
            onClick={() => navigate("/signup")}
            className="px-3 py-1.5 bg-blue-600 text-white rounded-lg text-sm font-medium"
          >
            {t("navigation.signup")}
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-12">
        {/* Back Button */}
        <div className="mb-4 sm:mb-8">
          <button
            onClick={() => (window.location.href = "/")}
            className="flex items-center text-[#B22A2B] hover:text-[#8a2122] transition-colors text-sm sm:text-base"
          >
            <svg
              className="w-4 h-4 sm:w-5 sm:h-5 mr-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M10 19l-7-7m0 0l7-7m-7 7h18"
              />
            </svg>
            {t("pages.browseEvents.backToPebble")}
          </button>
        </div>

        {/* Location Toggle Button (shown when prompt is dismissed) */}
        {!showLocationPrompt && locationPermission === null && (
          <div className="flex justify-end mb-4">
            <button
              onClick={() => setShowLocationPrompt(true)}
              className="flex items-center gap-2 px-4 py-2 bg-white border border-grey-300 rounded-lg hover:bg-grey-50 transition-colors text-sm font-medium text-grey-700"
            >
              <Navigation size={16} />
              {t("pages.browseEvents.enableLocation")}
            </button>
          </div>
        )}

        {/* Location Permission Prompt */}
        {showLocationPrompt && (
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-6 mb-8">
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-start gap-3 flex-1">
                <Navigation className="text-blue-600 mt-1" size={24} />
                <div>
                  <h3 className="text-lg font-semibold text-grey-900 mb-1">
                    {t("pages.browseEvents.enableLocationTitle")}
                  </h3>
                  <p className="text-grey-600 mb-4">
                    {t("pages.browseEvents.enableLocationDesc")}
                  </p>
                  <div className="flex gap-3">
                    <button
                      onClick={requestLocation}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      {t("pages.browseEvents.enableLocation")}
                    </button>
                    <button
                      onClick={() => setShowLocationPrompt(false)}
                      className="px-4 py-2 bg-white text-grey-700 border border-grey-300 rounded-lg hover:bg-grey-50 transition-colors"
                    >
                      {t("pages.browseEvents.noThanks")}
                    </button>
                  </div>
                </div>
              </div>
              <button
                onClick={() => setShowLocationPrompt(false)}
                className="text-grey-400 hover:text-grey-600"
              >
                <X size={20} />
              </button>
            </div>
          </div>
        )}

        {/* Call to Action for Non-Logged In Users - Hidden on mobile (shown in banner above) */}
        <div className="hidden sm:block bg-white rounded-xl shadow-sm p-6 mb-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div>
              <h2 className="text-2xl font-semibold text-grey-900 mb-2">
                Ready to sell at local events?
              </h2>
              <p className="text-grey-600">
                Create a free vendor account to apply to markets and festivals
                in your area.
              </p>
            </div>
            <div className="flex gap-4">
              <button
                onClick={() => navigate("/login")}
                className="px-6 py-2 text-grey-700 bg-grey-100 rounded-lg hover:bg-grey-200 transition-colors"
              >
                Log In
              </button>
              <button
                onClick={() => navigate("/signup")}
                className="px-6 py-3 text-white bg-primary-400 rounded-lg hover:bg-primary-500 transition-colors flex items-center gap-2 font-medium"
              >
                Create Vendor Account <ArrowRight size={16} />
              </button>
            </div>
          </div>
        </div>

        {/* Results Count */}
        {!isLoading && filteredEvents.length > 0 && (
          <div className="mb-4 flex items-center justify-between">
            <p className="text-sm text-grey-600">
              {filteredEvents.length} event
              {filteredEvents.length !== 1 ? "s" : ""} found
              {searchQuery && <span> for "{searchQuery}"</span>}
            </p>
          </div>
        )}

        {/* Loading State */}
        {isLoading && (
          <div className="text-center py-12 flex flex-col items-center transition-opacity duration-300">
            <p className="text-grey-600">Loading events...</p>
          </div>
        )}

        {/* Events Grid */}
        {!isLoading && filteredEvents.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredEvents.map((event) => {
              const isAcceptingVendors = event.applicationDeadline
                ? new Date(event.applicationDeadline) > new Date()
                : false;
              const city = event.location
                ? event.location.split(",")[0].trim()
                : null;

              return (
                <div
                  key={event.id}
                  className="bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-shadow duration-300 border border-grey-200"
                >
                  {/* Event Image with Badge */}
                  {event.eventCoverPhoto ? (
                    <div className="relative h-48 w-full">
                      <img
                        src={event.eventCoverPhoto}
                        alt={event.eventName}
                        className="w-full h-full object-cover"
                      />
                      {isAcceptingVendors && (
                        <div className="absolute top-4 right-4">
                          <span className="bg-blue-600 text-white px-3 py-1.5 rounded-full text-sm font-medium shadow-sm">
                            Accepting Vendors
                          </span>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="relative h-48 w-full bg-grey-200 flex items-center justify-center">
                      <img
                        src={placeholderImage}
                        alt="Event"
                        className="w-24 h-24 opacity-50"
                      />
                      {isAcceptingVendors && (
                        <div className="absolute top-4 right-4">
                          <span className="bg-blue-600 text-white px-3 py-1.5 rounded-full text-sm font-medium shadow-sm">
                            Accepting Vendors
                          </span>
                        </div>
                      )}
                    </div>
                  )}

                  <div className="p-6">
                    <div className="flex flex-col">
                      {/* Event Type Badge */}
                      {event.eventType && (
                        <span className="text-sm font-medium text-green-700 mb-2">
                          {event.eventType}
                        </span>
                      )}

                      {/* Event Name */}
                      <h3 className="text-2xl font-bold text-grey-900 mb-3">
                        {event.eventName}
                      </h3>

                      {/* Key Info Section */}
                      <div className="space-y-2 mb-4">
                        {/* Location */}
                        {event.location && (
                          <div className="flex items-center text-grey-600">
                            <MapPin
                              size={20}
                              className="mr-2 text-grey-400 flex-shrink-0"
                            />
                            <span className="text-base">{event.location}</span>
                            {event.distance && (
                              <span className="text-xs text-grey-500 ml-2">
                                • {event.distance.toFixed(1)} mi away
                              </span>
                            )}
                          </div>
                        )}

                        {/* Date */}
                        <div className="flex items-center text-grey-600">
                          <Calendar
                            size={20}
                            className="mr-2 text-grey-400 flex-shrink-0"
                          />
                          <span className="text-base">
                            {event.startDate ? (
                              <>
                                {new Date(event.startDate).toLocaleDateString(
                                  "en-US",
                                  {
                                    weekday: "long",
                                    year: "numeric",
                                    month: "long",
                                    day: "numeric",
                                  },
                                )}
                                {event.endDate &&
                                  event.endDate !== event.startDate && (
                                    <span className="text-sm text-grey-500">
                                      {" "}
                                      to{" "}
                                      {new Date(
                                        event.endDate,
                                      ).toLocaleDateString("en-US", {
                                        month: "short",
                                        day: "numeric",
                                      })}
                                    </span>
                                  )}
                              </>
                            ) : (
                              "Date not set"
                            )}
                          </span>
                        </div>

                        {/* Application Deadline */}
                        {isAcceptingVendors && event.applicationDeadline && (
                          <div className="flex items-center text-blue-600 font-medium">
                            <CheckCircle
                              size={20}
                              className="mr-2 flex-shrink-0"
                            />
                            <span className="text-base">
                              Apply by{" "}
                              {new Date(
                                event.applicationDeadline,
                              ).toLocaleDateString("en-US", {
                                month: "long",
                                day: "numeric",
                                year: "numeric",
                              })}
                            </span>
                          </div>
                        )}
                      </div>

                      {/* Description */}
                      {(event.eventDescription || event.description) && (
                        <p className="text-grey-600 text-sm mb-4 line-clamp-2">
                          {event.eventDescription || event.description}
                        </p>
                      )}

                      {/* View Details Button - Larger touch target on mobile */}
                      <button
                        onClick={() => handleEventClick(event.id)}
                        className="w-full sm:w-auto inline-flex items-center justify-center px-6 py-3 sm:py-2 bg-[#B22A2B] text-white rounded-lg hover:bg-[#8a2122] transition-colors text-base sm:text-sm font-semibold active:scale-[0.98]"
                      >
                        View & Apply
                        <ArrowRight size={18} className="ml-2" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Empty State */}
        {!isLoading && filteredEvents.length === 0 && (
          <div className="text-center py-12">
            <p className="text-grey-600 text-lg">
              No events are currently published.
              <br />
              Check back soon for updates!
            </p>
          </div>
        )}
      </div>

      {/* Auth Modal - Mobile Optimized */}
      <dialog
        id="auth-modal"
        className="rounded-xl shadow-xl p-0 backdrop:bg-gray-800/50 m-4 sm:m-auto max-w-md w-[calc(100%-2rem)]"
      >
        <div className="w-full bg-white rounded-xl overflow-hidden relative">
          <button
            onClick={() => document.getElementById("auth-modal").close()}
            className="absolute top-4 right-4 text-grey-400 hover:text-grey-600 transition-colors p-1"
          >
            <X size={24} />
          </button>
          <div className="p-6 sm:p-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-full bg-primary-100 flex items-center justify-center">
                <Store size={24} className="text-primary-600" />
              </div>
              <h3 className="text-xl font-semibold text-grey-900 pr-8">
                Apply as a Vendor
              </h3>
            </div>
            <p className="text-grey-600 mb-6">
              Create a free account to view event details, contact organizers,
              and submit your vendor application.
            </p>
            <div className="space-y-3">
              <button
                onClick={() => navigate("/signup")}
                className="w-full py-3 px-4 border border-transparent rounded-lg shadow-sm text-base font-semibold text-white bg-[#B84141] hover:bg-[#923434] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#B84141] active:scale-[0.98]"
              >
                Create Vendor Account
              </button>
              <button
                onClick={() => navigate("/login")}
                className="w-full py-3 px-4 border border-grey-300 rounded-lg shadow-sm text-base font-medium text-grey-700 bg-white hover:bg-grey-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#B84141]"
              >
                Already have an account? Log In
              </button>
            </div>
          </div>
        </div>
      </dialog>
    </div>
  );
};

export default BrowseEvents;
